import React, { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/Page";
import { UNITS } from "@/pages/invoices/invoiceConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 focus-visible:ring-cyan-500";
const blank = () => ({ description: "", qty: 1, unit: "Month", unit_price: 0, gst_mode: "exclusive" });

export default function NewInvoice() {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState("");
  const [due, setDue] = useState("");
  const [terms, setTerms] = useState("");
  const [notes, setNotes] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [items, setItems] = useState([blank()]);

  const { data: customers } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const { data: settings } = useQuery({ queryKey: ["inv-settings"], queryFn: async () => (await api.get("/invoices/settings")).data });
  const rate = (settings?.company?.gst_registered ? settings?.gst_rate : 0) || 0;

  const totals = useMemo(() => {
    let sub = 0, gst = 0;
    items.forEach((it) => {
      const gross = Math.round(Number(it.qty || 0) * Number(it.unit_price || 0) * 100);
      if (!rate || it.gst_mode === "none") { sub += gross; }
      else if (it.gst_mode === "inclusive") { const net = Math.round(gross / (1 + rate / 100)); sub += net; gst += gross - net; }
      else { sub += gross; gst += Math.round(gross * rate / 100); }
    });
    return { sub, gst, total: sub + gst };
  }, [items, rate]);
  const fmt = (c) => `$${(c / 100).toFixed(2)}`;

  const save = useMutation({
    mutationFn: async () => (await api.post("/invoices", {
      customer_id: customer, due_date: due || null, payment_terms: terms || null, notes,
      period_start: periodStart || null, period_end: periodEnd || null,
      line_items: items.map((i) => ({ ...i, qty: Number(i.qty), unit_price: Number(i.unit_price) })),
    })).data,
    onSuccess: (inv) => { toast.success(`Invoice ${inv.number} created`); navigate(`/invoices/${inv.id}`); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const upd = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  return (
    <div data-testid="new-invoice-page">
      <button onClick={() => navigate("/invoices")} className="mb-3 flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-300" data-testid="back-to-invoices"><ArrowLeft className="h-4 w-4" /> Invoices</button>
      <PageHeader title="New Invoice" subtitle="Draft an invoice from an existing customer." />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs text-slate-400">Customer</Label>
                <Select value={customer} onValueChange={setCustomer}>
                  <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="invoice-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">{(customers || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-400">Due date</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={`mt-1 ${inputCls}`} data-testid="invoice-due-input" /></div>
              <div><Label className="text-xs text-slate-400">Payment terms</Label><Input value={terms} onChange={(e) => setTerms(e.target.value)} placeholder={settings?.default_payment_terms || "Net 14 days"} className={`mt-1 ${inputCls}`} data-testid="invoice-terms-input" /></div>
              <div><Label className="text-xs text-slate-400">Service period start</Label><Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className={`mt-1 ${inputCls}`} data-testid="invoice-period-start" /></div>
              <div><Label className="text-xs text-slate-400">Service period end</Label><Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className={`mt-1 ${inputCls}`} data-testid="invoice-period-end" /></div>
            </div>
          </div>

          <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-200">Line items</span>
              <Button size="sm" variant="outline" onClick={() => setItems([...items, blank()])} className="border-[#2D3F62] bg-transparent text-cyan-300" data-testid="add-line-btn"><Plus className="mr-1 h-3.5 w-3.5" /> Add line</Button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2" data-testid={`line-${i}`}>
                  <Input placeholder="Description" value={it.description} onChange={(e) => upd(i, "description", e.target.value)} className={`col-span-12 md:col-span-4 ${inputCls}`} data-testid={`line-desc-${i}`} />
                  <Input type="number" placeholder="Qty" value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} className={`col-span-3 md:col-span-1 ${inputCls}`} data-testid={`line-qty-${i}`} />
                  <Select value={it.unit} onValueChange={(v) => upd(i, "unit", v)}><SelectTrigger className={`col-span-4 md:col-span-2 ${inputCls}`} data-testid={`line-unit-${i}`}><SelectValue /></SelectTrigger><SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
                  <Input type="number" placeholder="Rate" value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} className={`col-span-3 md:col-span-2 ${inputCls}`} data-testid={`line-rate-${i}`} />
                  <Select value={it.gst_mode} onValueChange={(v) => upd(i, "gst_mode", v)}><SelectTrigger className={`col-span-8 md:col-span-2 ${inputCls}`} data-testid={`line-gst-${i}`}><SelectValue /></SelectTrigger><SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100"><SelectItem value="exclusive">GST excl</SelectItem><SelectItem value="inclusive">GST incl</SelectItem><SelectItem value="none">No GST</SelectItem></SelectContent></Select>
                  <button onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="col-span-4 flex items-center justify-center text-rose-400 hover:text-rose-300 md:col-span-1" data-testid={`line-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <div><Label className="mt-3 block text-xs text-slate-400">Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} className={`mt-1 ${inputCls}`} data-testid="invoice-notes-input" /></div>
          </div>
        </div>

        <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between text-slate-400"><span>Subtotal (ex GST)</span><span className="text-slate-200" data-testid="preview-subtotal">{fmt(totals.sub)}</span></div>
            <div className="flex justify-between text-slate-400"><span>GST ({rate}%)</span><span className="text-slate-200" data-testid="preview-gst">{fmt(totals.gst)}</span></div>
            <div className="flex justify-between border-t border-[#1E2D4A] pt-2 font-semibold text-cyan-300"><span>Total</span><span data-testid="preview-total">{fmt(totals.total)}</span></div>
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !customer || !items.some((i) => i.description)} className="mt-4 w-full bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="save-invoice-btn">Create Draft</Button>
        </div>
      </div>
    </div>
  );
}
